import React, { useEffect, useState } from "react";
import "./style.scss";
import "./style.darkmode.scss";
import { Button, CircularProgress, Alert } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setAlert, setCreditCardForm } from "../../../redux/actions";
import {
    AddressElement,
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

const CreditCardForm = ({
    intent = "",
    puLogin = "",
    setShow,
    prefilled = false,
    canCancel = true,
    onCancel = null,
}) => {
    const dispatch = useDispatch();
    const stripe = useStripe();
    const elements = useElements();

    const darkmode = useSelector((state) => state.privaGlobal.darkmode);
    const mode = useSelector((state) => state.privaGlobal.creditCardForm.mode);
    const defaultCard = useSelector(
        (state) => state.privaGlobal.creditCardForm.card
    );
    const formLoading = useSelector(
        (state) => state.privaGlobal.creditCardForm.formLoading
    );

    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState(null);
    const [billingDetails, setBillingDetails] = useState({
        name: "",
        city: "",
        state: "",
        postal_code: "",
        country: "",
        line1: "",
        line2: "",
    });
    const [addressError, setAddressError] = useState("");
    const [isGettingInfo, setIsGettingInfo] = useState(false);
    const [defaultAddress, setDefaultAddress] = useState({});
    const [cardCompleted, setCardCompleted] = useState(false);
    const [expiryCompleted, setExpiryCompleted] = useState(false);
    const [cvcCompleted, setCvcCompleted] = useState(false);

    const checkForCard = async () => {
        await axios.get("/api/cards/existing").then((response) => {
            if (response.data && response.data.data.existing_card) {
                dispatch(
                    setAlert({
                        show: true,
                        type: "info",
                        body: "In acordance with mandatory requirements from the bank security and protection for debit and credit cards please re-enter your card and address details in the new payment card form.",
                    })
                );
            }
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return; // Stripe.js has not yet loaded.
        }

        if (mode == "add") {
            if (!cardCompleted) {
                setError("Card number is required");
                return false;
            }
            if (!expiryCompleted) {
                setError("Card expiry is required");
                return false;
            }
            if (!cvcCompleted) {
                setError("Card CVC is required");
                return false;
            }
        }

        if (!billingDetails.name) {
            setAddressError("Name is required");
            setIsBusy(false);
            return;
        }

        if (!billingDetails.country) {
            setAddressError("Country is required");
            setIsBusy(false);
            return;
        }

        /*
        if (!billingDetails.line1) {
            setAddressError("Address line1 is required");
            setIsBusy(false);
            return;
        }

        if (!billingDetails.city) {
            setAddressError("City is required");
            setIsBusy(false);
            return;
        }

        
        if (!billingDetails.state) {
            setAddressError("State is required");
            setIsBusy(false);
            return;
        }

        if (!billingDetails.postal_code) {
            setAddressError("Postal Code is required");
            setIsBusy(false);
            return;
        }
        */

        setIsBusy(true);

        if (mode == "add") {
            const cardNumberElement = elements.getElement(CardNumberElement);
            const { error: paymentError, paymentMethod } =
                await stripe.createPaymentMethod({
                    type: "card",
                    card: cardNumberElement,
                    billing_details: {
                        name: billingDetails.name,
                        address: {
                            line1: billingDetails.line1,
                            line2: billingDetails.line2,
                            city: billingDetails.city,
                            state: billingDetails.state,
                            postal_code: billingDetails.postal_code,
                            country: billingDetails.country,
                        },
                    },
                });

            if (paymentError) {
                setError(paymentError.message);
                setIsBusy(false);
                return;
            }
            // attach to logged in user
            attachCard(paymentMethod.id);
        } else {
            axios
                .patch("/api/cards", {
                    line1: billingDetails.line1,
                    line2: billingDetails.line2,
                    city: billingDetails.city,
                    state: billingDetails.state,
                    postal_code: billingDetails.postal_code,
                    country: billingDetails.country,
                    name: billingDetails.name,
                })
                .then((response) => {
                    dispatch(
                        setAlert({
                            show: true,
                            type: "success",
                            body: "Card details updated",
                            onClose: () => {
                                setShow(false);
                                dispatch(
                                    setCreditCardForm({
                                        show: false,
                                        mode: "add",
                                    })
                                );
                                window.dispatchEvent(
                                    new Event("credit-card-form/success")
                                );
                            },
                        })
                    );
                })
                .catch((error) => {
                    console.error(error);
                    setShow(false);
                });
        }
    };

    const attachCard = async (pid) => {
        if (!pid) {
            setError("Unable to process card");
            return;
        }
        setIsBusy(true);
        await axios
            .post("/api/add-card", {
                payment_method_id: pid,
            })

            .then((response) => {
                setIsBusy(false);

                let message = "Card details updated";
                if (intent) {
                    message = `Card details added. You may continue to ${intent} ${puLogin}`;
                }
                dispatch(
                    setAlert({
                        show: true,
                        type: "success",
                        body: message,
                        onClose: () => {
                            setShow(false);
                            dispatch(setCreditCardForm({ show: false }));
                        },
                    })
                );

                window.dispatchEvent(new Event("credit-card-form/success"));
            })
            .catch((error) => {
                console.error(error);
                setIsBusy(false);

                if (error.response) {
                    if (error.response.data.message) {
                        dispatch(
                            setAlert({
                                show: true,
                                type: "error",
                                body:
                                    error.response.data.message ||
                                    "Your card was declined.",
                            })
                        );
                    }
                }
            });
    };

    const handleClose = () => {
        if (onCancel && typeof onCancel === "function") {
            onCancel();
        } else {
            setShow(false);
        }
    };

    const handleAddressChange = (event) => {
        setAddressError("");
        const address = event.value.address;
        const name = event.value.name;

        setBillingDetails({ ...billingDetails, ...address, name });
    };

    const cardOptions = {
        style: {
            base: {
                color: "#32325d",
                fontSize: "16px",
                padding: "10px",
                border: `1px solid ${darkmode ? "#424353" : "#e6e6e6"}`, // Border styling
                borderRadius: "4px", // Rounded corners
                color: `${darkmode ? "#fff" : "#32325d"}`,
                lineHeight: "18px",
            },
            invalid: {
                color: "#fa755a",
                border: "1px solid #fa755a", // Border when invalid
            },
            complete: {
                border: "1px solid #4CAF50", // Border when input is complete
            },
        },
    };

    const labelStyle = {
        color: "#32325d",
        fontSize: "14.88px",
        lineHeight: "17.112px",
        fontWeight: 400,
        fontFamily: "Segoe UI",
    };

    useEffect(() => {
        if (defaultCard && Object.keys(defaultCard).length > 0) {
            setDefaultAddress({
                address: {
                    city: defaultCard.billing_details.address.city,
                    country: defaultCard.billing_details.address.country,
                    line1: defaultCard.billing_details.address.line1,
                    line2: defaultCard.billing_details.address.line2,
                    postal_code:
                        defaultCard.billing_details.address.postal_code,
                    state: defaultCard.billing_details.address.state,
                },
                name: defaultCard.billing_details.name,
            });
        }
    }, [defaultCard]);

    const handleCardChange = (event) => {
        if (event.elementType === "cardNumber" && event.complete) {
            setError("");
            setCardCompleted(true);
            return false;
        }

        if (event.elementType === "cardExpiry" && event.complete) {
            setError("");
            setExpiryCompleted(true);
            return false;
        }

        if (event.elementType === "cardCvc" && event.complete) {
            setError("");
            setCvcCompleted(true);
            return false;
        }
    };

    // useEffect(() => {
    //     checkForCard();
    // }, []);

    return (
        <div className="credit-card-form">
            <div
                css={css`
                    font-size: 20px;
                    font-weight: bold;
                    text-align: center;
                `}
            >
                CARD PAYMENT METHOD
            </div>

            <div
                css={css`
                    font-size: 14px;
                    padding: 12px;
                    text-align: center;
                `}
            >
                Your payment is safely protected by Stripe's 3D Secure system.
                We never store your card information. Only your bank sees it for
                approval. <i className="fa-solid fa-lock-keyhole"></i>
            </div>

            <div
                css={css`
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                `}
            >
                <div
                    css={css`
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        padding: 12px;
                    `}
                >
                    <img
                        src="/images/cards.png"
                        alt="Cards"
                        style={{ width: 160 }}
                        border="0"
                    />
                </div>

                {!formLoading ? (
                    <form onSubmit={handleSubmit}>
                        {mode === "add" ? (
                            <React.Fragment>
                                <div
                                    style={{
                                        display: "block",
                                        marginBottom: 12,
                                    }}
                                >
                                    <label
                                        style={labelStyle}
                                        htmlFor="card-element"
                                    >
                                        Card Number
                                    </label>
                                    <div
                                        style={cardOptions.style.base}
                                        className="card-input Input"
                                    >
                                        <CardNumberElement
                                            options={{
                                                ...cardOptions,
                                                showIcon: true,
                                            }}
                                            style={cardOptions.style.base}
                                            onChange={handleCardChange}
                                        />
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: 12,
                                        marginBottom: 12,
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <label
                                            htmlFor="card-expiry-element"
                                            style={labelStyle}
                                        >
                                            Expiry
                                        </label>
                                        <div
                                            style={cardOptions.style.base}
                                            className="card-input Input"
                                        >
                                            <CardExpiryElement
                                                options={cardOptions}
                                                onChange={handleCardChange}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label
                                            htmlFor="card-cvc-element"
                                            style={labelStyle}
                                        >
                                            CVC
                                        </label>
                                        <div
                                            style={cardOptions.style.base}
                                            className="card-input Input"
                                        >
                                            <CardCvcElement
                                                options={{
                                                    ...cardOptions,
                                                    placeholder: "",
                                                }}
                                                onChange={handleCardChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </React.Fragment>
                        ) : null}

                        <AddressElement
                            options={{
                                mode: "billing",
                                autocomplete: {
                                    mode: "disabled",
                                },
                                style: {
                                    lineHeight: "18px",
                                },
                                defaultValues: defaultAddress,
                            }}
                            onChange={handleAddressChange}
                        />

                        {error || addressError ? (
                            <div style={{ marginTop: 12 }}>
                                <Alert severity="error">
                                    {" "}
                                    {error || addressError}
                                </Alert>
                            </div>
                        ) : null}

                        <div
                            css={css`
                                padding: 12px;
                                font-size: 12px;
                                text-align: center;
                            `}
                        >
                            <img src="/images/stripe.svg" width={50} /> Secured
                            by SSL /TLS Encyoted
                        </div>
                    </form>
                ) : (
                    <div
                        style={{
                            display: "flex",
                            flex: 1,
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress size={48} />
                    </div>
                )}
            </div>

            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 18,
                    gap: 12,
                }}
            >
                {!isBusy && canCancel ? (
                    <Button
                        variant="outlined"
                        disabled={isBusy}
                        onClick={handleClose}
                        sx={{
                            borderRadius: 20,
                            fontWeight: "bold",
                        }}
                    >
                        Cancel
                    </Button>
                ) : null}
                <Button
                    variant="contained"
                    disabled={
                        isBusy ||
                        !stripe ||
                        !elements ||
                        formLoading ||
                        isGettingInfo
                    }
                    onClick={handleSubmit}
                    sx={{ borderRadius: 20, fontWeight: "bold" }}
                >
                    {isBusy ? "Saving Card Details..." : "Confirm Securely"}
                </Button>
            </div>
        </div>
    );
};

export default CreditCardForm;
